/**
 * Responsive board layout — viewport-sized canvas with scenery margins,
 * mockup column grid 1:4:1 horizontally (`1fr 4fr 1fr`),
 * grid height locked to 4.5× grid unit (core box = 4:3); extra vertical space
 * split evenly. Extra horizontal viewport width expands side columns (play column
 * size unchanged); side chrome content stays centred in the wider strips.
 */
import * as C from "../config/constants.js";

/** Side column width as a fraction of grid width (1/6). */
export const SIDE_COLUMN_FRACTION = 1 / 6;

/** Play column width as a fraction of grid width (4/6). */
export const PLAY_COLUMN_FRACTION = 4 / 6;

/** Total grid width in side-column units (1 + 4 + 1). */
export const COLUMN_UNITS_TOTAL = 6;

/** Grid / play area height = side column width × this multiplier.
 *  6 wide × 4.5 tall ⇒ core box ratio 4:3 (matches day.html / night.html). */
export const GRID_HEIGHT_SIDE_MULTIPLIER = 4.5;

/**
 * Play column width:height (4U wide × 4.5U tall → ~8:9 portrait).
 */
export const MOCKUP_PLAY_AREA_RATIO = PLAY_COLUMN_FRACTION / (GRID_HEIGHT_SIDE_MULTIPLIER * SIDE_COLUMN_FRACTION);

/** @deprecated Use {@link MOCKUP_PLAY_AREA_RATIO}. */
export const PLAY_AREA_RATIO = MOCKUP_PLAY_AREA_RATIO;

export const DEFAULT_BOTTOM_BAR_HEIGHT_PX = 28;

/** Minimum grid unit (side column width) in px. */
export const MIN_GRID_UNIT_PX = 20;

/**
 * Minimum top/bottom scenery band per side, as a fraction of `gridUnitPx`
 * (~0.3× side-column width). Grid shrinks when needed to honour this floor.
 */
export const SCENERY_MIN_VERTICAL_SIDE_UNIT_FRACTION = 0.3;

function getMinVerticalSceneryPx(unit) {
  return Math.floor(unit * SCENERY_MIN_VERTICAL_SIDE_UNIT_FRACTION);
}

function verticalLayoutFits(unit, usableH, dockH) {
  var gridH = unit * GRID_HEIGHT_SIDE_MULTIPLIER;
  var minSceneryTotal = getMinVerticalSceneryPx(unit) * 2;
  return usableH >= gridH + dockH + minSceneryTotal;
}

/**
 * @param {{ width: number, height: number }} viewport
 * @param {{ bottomBarHeightPx?: number, dockHeightPx?: number }} [opts]
 */
export function computeResponsiveBoard(viewport, opts) {
  opts = opts || {};
  var vw = Math.max(320, Math.floor(viewport.width || 0));
  var vh = Math.max(240, Math.floor(viewport.height || 0));
  var bottomBarH = opts.bottomBarHeightPx != null
    ? Math.max(0, Math.floor(Number(opts.bottomBarHeightPx)))
    : DEFAULT_BOTTOM_BAR_HEIGHT_PX;
  var dockH = opts.dockHeightPx != null
    ? Math.max(0, Math.floor(Number(opts.dockHeightPx)))
    : 0;

  var boardW = vw;
  var boardH = vh;
  var bottomBarY = boardH - bottomBarH;
  var usableH = bottomBarY;

  // Core box is 4:3 (COLUMN_UNITS_TOTAL wide × GRID_HEIGHT_SIDE_MULTIPLIER tall).
  // Cap the unit by width (core ≤ vw) and height (grid ≤ usable) independently.
  var unitMaxW = Math.floor(vw / COLUMN_UNITS_TOTAL);
  var unitMaxH = Math.floor((usableH - dockH) / GRID_HEIGHT_SIDE_MULTIPLIER);
  var unit = Math.min(unitMaxW, unitMaxH);
  if (unit < MIN_GRID_UNIT_PX) {
    unit = Math.max(
      MIN_GRID_UNIT_PX,
      Math.min(unitMaxW, unitMaxH),
    );
  }
  // Keep unit even so gridH = unit × 4.5 stays an integer pixel count.
  unit -= unit % 2;
  if (unit < 2) unit = 2;

  while (unit > MIN_GRID_UNIT_PX && !verticalLayoutFits(unit, usableH, dockH)) {
    unit -= 2;
  }
  if (!verticalLayoutFits(unit, usableH, dockH) && unit > 2) {
    unit = Math.max(2, MIN_GRID_UNIT_PX);
    unit -= unit % 2;
    if (unit < 2) unit = 2;
  }

  while (unit + 2 <= unitMaxW && verticalLayoutFits(unit + 2, usableH, dockH)) {
    unit += 2;
  }

  var gridW = unit * COLUMN_UNITS_TOTAL;
  var baseSideW = unit;
  var playW = unit * 4;
  var gridH = unit * GRID_HEIGHT_SIDE_MULTIPLIER;

  var contentH = gridH + dockH;
  var sceneryTotal = Math.max(0, usableH - contentH);
  var sceneryTop = Math.floor(sceneryTotal / 2);
  var sceneryBottom = sceneryTotal - sceneryTop;

  // Extra horizontal space expands the side columns; play column size stays fixed.
  var extraHoriz = Math.max(0, boardW - gridW);
  var leftExtra = Math.floor(extraHoriz / 2);
  var rightExtra = extraHoriz - leftExtra;
  var leftW = baseSideW + leftExtra;
  var rightW = baseSideW + rightExtra;
  var coreW = leftW + playW + rightW;
  var sceneryLeft = 0;
  var sceneryRight = 0;
  var coreX = 0;
  var gridY = sceneryTop;
  var dockY = gridY + gridH;

  return {
    boardWidth: boardW,
    boardHeight: boardH,
    sceneryMargins: {
      left: sceneryLeft,
      right: sceneryRight,
      top: sceneryTop,
      bottom: sceneryBottom,
    },
    coreRect: {
      x: coreX,
      y: gridY,
      width: coreW,
      height: gridH,
    },
    playArea: {
      x: coreX + leftW,
      y: gridY,
      width: playW,
      height: gridH,
    },
    leftStrip: {
      x: coreX,
      y: gridY,
      width: leftW,
      height: gridH,
    },
    rightStrip: {
      x: coreX + leftW + playW,
      y: gridY,
      width: rightW,
      height: gridH,
    },
    bottomBar: {
      x: 0,
      y: bottomBarY,
      width: boardW,
      height: bottomBarH,
    },
    evolutionDockBounds: dockH > 0
      ? { x: 0, y: dockY, width: boardW, height: dockH }
      : null,
    bottomBarHeightPx: bottomBarH,
    playAreaRatio: playW / gridH,
    coreRatio: coreW / gridH,
    gridUnitPx: unit,
    /** Base 1:4:1 grid width (before side-column expansion); use for mockup UI scale. */
    gridWidthPx: gridW,
  };
}

/**
 * Mockup grid width for UI scale — stable when side columns absorb extra viewport width.
 * @param {{ gridWidthPx?: number, gridUnitPx?: number, coreRect?: { width: number } }} boardOrLayout
 */
export function resolveGridWidthPx(boardOrLayout) {
  var src = boardOrLayout || {};
  if (src.gridWidthPx != null && src.gridWidthPx > 0) {
    return Math.floor(Number(src.gridWidthPx));
  }
  if (src.gridUnitPx != null && src.gridUnitPx > 0) {
    return Math.floor(Number(src.gridUnitPx)) * COLUMN_UNITS_TOTAL;
  }
  if (src.coreRect && src.coreRect.width > 0) {
    return Math.floor(src.coreRect.width);
  }
  return COLUMN_UNITS_TOTAL * MIN_GRID_UNIT_PX;
}

/** Play side inset fraction (vertical layout content padding). */
export const PLAY_SIDE_INSET_FRACTION = 0.05;

/**
 * Horizontal padding inside the play band (vertical layout).
 * @param {{ width: number }} play
 * @param {{ playSideInsetPx?: number, playSideInsetFraction?: number, playContentArea?: { x: number, width: number } }} layout
 */
export function resolvePlaySideInsetPx(play, layout) {
  layout = layout || {};
  if (layout.playSideInsetPx != null && layout.playSideInsetPx >= 0) {
    return Math.floor(Number(layout.playSideInsetPx));
  }
  var content = layout.playContentArea;
  if (content && play && isFinite(play.x) && isFinite(content.x) && isFinite(content.width)) {
    var fromContent = Math.floor(content.x - play.x);
    if (fromContent >= 0) return fromContent;
  }
  var frac =
    layout.playSideInsetFraction != null
      ? Number(layout.playSideInsetFraction)
      : PLAY_SIDE_INSET_FRACTION;
  if (!play || !isFinite(play.width) || play.width <= 0) return 0;
  return Math.max(0, Math.floor(play.width * frac));
}

/**
 * Compact 1:4:1 grid columns for sky/sun anchors (horizontal). In vertical mode,
 * rise/set use the 5% play side inset bands at the play area edges.
 * @param {{
 *   layoutMode?: string,
 *   coreRect?: { x: number, y: number, width: number, height: number },
 *   playArea?: { x: number, y: number, width: number, height: number },
 *   playContentArea?: { x: number, y: number, width: number, height: number },
 *   playSideInsetPx?: number,
 *   playSideInsetFraction?: number,
 *   leftStrip?: { x: number, width: number },
 *   rightStrip?: { x: number, width: number },
 *   gridUnitPx?: number,
 *   gridWidthPx?: number,
 * }} layout
 */
export function resolveSkyAnchorColumns(layout) {
  layout = layout || {};
  var core = layout.coreRect || { x: 0, y: 0, width: 720, height: 540 };
  var play = layout.playArea || core;

  if (layout.layoutMode === "vertical") {
    var inset = resolvePlaySideInsetPx(play, layout);
    return {
      coreRect: core,
      playArea: play,
      leftStrip: { x: play.x, width: inset },
      rightStrip: { x: play.x + play.width - inset, width: inset },
    };
  }

  var unit = Number(layout.gridUnitPx);
  var gridW = resolveGridWidthPx(layout);

  if (unit > 0 && gridW > 0) {
    var inset = Math.max(0, Math.floor((core.width - gridW) / 2));
    var playW = unit * 4;
    var gridX = core.x + inset;
    return {
      coreRect: { x: gridX, y: core.y, width: gridW, height: core.height },
      playArea: play,
      leftStrip: { x: gridX, width: unit },
      rightStrip: { x: gridX + unit + playW, width: unit },
    };
  }

  return {
    coreRect: core,
    playArea: play,
    leftStrip: layout.leftStrip || { x: core.x, width: core.width / 6 },
    rightStrip: layout.rightStrip || {
      x: core.x + core.width * (5 / 6),
      width: core.width / 6,
    },
  };
}

/**
 * Assert layout relationships for tests.
 * @param {ReturnType<typeof computeResponsiveBoard>} board
 */
export function validateResponsiveBoard(board) {
  var play = board.playArea;
  var left = board.leftStrip;
  var right = board.rightStrip;
  var core = board.coreRect;
  var bw = board.boardWidth;

  if (left.width + play.width + right.width !== core.width) {
    throw new Error("column widths do not sum to core width");
  }
  var unit = board.gridUnitPx;
  var sideTol = 1;
  if (left.width < unit - sideTol) {
    throw new Error("left strip narrower than grid unit: " + left.width + " vs " + unit);
  }
  if (right.width < unit - sideTol) {
    throw new Error("right strip narrower than grid unit: " + right.width + " vs " + unit);
  }
  if (Math.abs(play.width - unit * 4) > sideTol) {
    throw new Error("play area not 4× grid unit: " + play.width + " vs " + unit * 4);
  }
  if (Math.abs(core.width - bw) > 1) {
    throw new Error("core width should match board width: " + core.width + " vs " + bw);
  }
  if (board.sceneryMargins.left !== 0 || board.sceneryMargins.right !== 0) {
    throw new Error("horizontal scenery should be absorbed by side columns");
  }
  if (play.height !== unit * GRID_HEIGHT_SIDE_MULTIPLIER) {
    throw new Error(
      "grid height not 4.5× grid unit: " + play.height + " vs " + unit * GRID_HEIGHT_SIDE_MULTIPLIER,
    );
  }
  if (core.height !== play.height) {
    throw new Error("core height must match play/grid height");
  }
  if (left.height !== play.height || right.height !== play.height) {
    throw new Error("column heights must match grid height");
  }
  if (Math.abs(play.width / play.height - MOCKUP_PLAY_AREA_RATIO) > 0.02) {
    throw new Error("play area ratio off: " + (play.width / play.height));
  }
  if (Math.abs(board.sceneryMargins.top - board.sceneryMargins.bottom) > 1) {
    throw new Error("vertical scenery not evenly split");
  }
  var minVert = getMinVerticalSceneryPx(board.gridUnitPx);
  var vertTotal = board.sceneryMargins.top + board.sceneryMargins.bottom;
  var requiredVert = minVert * 2;
  if (vertTotal >= requiredVert) {
    if (board.sceneryMargins.top < minVert - 1 || board.sceneryMargins.bottom < minVert - 1) {
      throw new Error(
        "vertical scenery below minimum: top=" + board.sceneryMargins.top +
        " bottom=" + board.sceneryMargins.bottom + " min=" + minVert,
      );
    }
  }
  if (core.x + core.width > bw + 1) {
    throw new Error("core exceeds board width");
  }
}
